import type { GameId } from '../api/types';
import { GAME_LIST, isGameId } from '../data/games';
import { RULE_PRESETS } from '../data/lotteryData';
import { RULE_SCHEMA, type RuleId, type RuleSchemaEntry, type SchemaField } from '../data/ruleSchema';
import type { TicketRule } from '../types/rules';
import { SIMPLE_VIBE_IDS, type SimpleVibeId } from './simpleVibeStacks';

export const RULE_CONFIG_STORAGE_KEY = 'chancey_rules_config';
export const RULE_CONFIG_METADATA_KEY = 'ruleConfig';
export const RULE_CONFIG_VERSION = 1;
export const DEFAULT_SIMPLE_VIBE: SimpleVibeId = 'balanced';

export interface PersistedRuleGameConfig {
  rules: TicketRule[];
  simpleVibe: SimpleVibeId;
}

export interface PersistedRuleConfig {
  version: typeof RULE_CONFIG_VERSION;
  byGame: Partial<Record<GameId, PersistedRuleGameConfig>>;
}

const RULE_IDS = Object.keys(RULE_SCHEMA) as RuleId[];
const RULE_ID_SET = new Set<string>(RULE_IDS);
const GAME_IDS = GAME_LIST.map((g) => g.id);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function genRuleInstanceId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function uniqueRuleInstanceId(value: unknown, seen: Set<string>): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  const candidate = trimmed || genRuleInstanceId();
  if (!seen.has(candidate)) {
    seen.add(candidate);
    return candidate;
  }
  let next = genRuleInstanceId();
  while (seen.has(next)) {
    next = genRuleInstanceId();
  }
  seen.add(next);
  return next;
}

function isRuleId(value: unknown): value is RuleId {
  return typeof value === 'string' && RULE_ID_SET.has(value);
}

export function isSimpleVibeId(value: unknown): value is SimpleVibeId {
  return typeof value === 'string' && (SIMPLE_VIBE_IDS as readonly string[]).includes(value);
}

export function sanitizeSimpleVibe(value: unknown): SimpleVibeId {
  return isSimpleVibeId(value) ? value : DEFAULT_SIMPLE_VIBE;
}

function sanitizeWeight(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 10;
  return Math.min(100, Math.max(0.01, value));
}

function sanitizeSliderValue(value: unknown, field: Extract<SchemaField, { type: 'slider' }>): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const bounded = Math.min(field.max, Math.max(field.min, value));
  if (field.step < 1) return bounded;
  return Math.round(bounded);
}

function sanitizeSettingsField(value: unknown, field: SchemaField): string | number | boolean | undefined {
  if (field.type === 'chips') {
    return field.options.some((o) => o.v === value) ? (value as string | number) : undefined;
  }
  if (field.type === 'toggle') {
    return typeof value === 'boolean' ? value : undefined;
  }
  return sanitizeSliderValue(value, field);
}

function sanitizeSettings(
  ruleId: RuleId,
  rawSettings: unknown,
  schema: RuleSchemaEntry = RULE_SCHEMA[ruleId]
): Record<string, string | number | boolean> {
  const settings = isRecord(rawSettings) ? rawSettings : {};
  const next: Record<string, string | number | boolean> = {};
  for (const field of schema.fields) {
    const fallback = schema.defaults[field.id];
    const sane = sanitizeSettingsField(settings[field.id], field);
    next[field.id] = sane ?? fallback;
  }
  return next;
}

export function sanitizeTicketRules(raw: unknown): TicketRule[] {
  if (!Array.isArray(raw)) return [];
  const rules: TicketRule[] = [];
  const seenInstanceIds = new Set<string>();
  for (const item of raw) {
    if (!isRecord(item) || !isRuleId(item.id)) continue;
    const preset = RULE_PRESETS.find((p) => p.id === item.id);
    if (!preset) continue;
    rules.push({
      instanceId: uniqueRuleInstanceId(item.instanceId, seenInstanceIds),
      id: item.id,
      name: preset.name,
      presetIcon: preset.icon,
      weight: sanitizeWeight(item.weight),
      enabled: typeof item.enabled === 'boolean' ? item.enabled : true,
      settings: sanitizeSettings(item.id, item.settings),
    });
  }
  return rules;
}

export function defaultRuleGameConfig(): PersistedRuleGameConfig {
  return {
    rules: [],
    simpleVibe: DEFAULT_SIMPLE_VIBE,
  };
}

export function sanitizeRuleGameConfig(raw: unknown): PersistedRuleGameConfig {
  const source = isRecord(raw) ? raw : {};
  return {
    rules: sanitizeTicketRules(source.rules),
    simpleVibe: sanitizeSimpleVibe(source.simpleVibe),
  };
}

export function emptyRuleConfig(): PersistedRuleConfig {
  return {
    version: RULE_CONFIG_VERSION,
    byGame: {},
  };
}

export function sanitizeRuleConfig(raw: unknown): PersistedRuleConfig {
  if (!isRecord(raw)) return emptyRuleConfig();
  if (raw.version !== RULE_CONFIG_VERSION) return emptyRuleConfig();
  const rawByGame = isRecord(raw.byGame) ? raw.byGame : {};
  const byGame: Partial<Record<GameId, PersistedRuleGameConfig>> = {};
  for (const gameId of GAME_IDS) {
    if (Object.prototype.hasOwnProperty.call(rawByGame, gameId) && isGameId(gameId)) {
      byGame[gameId] = sanitizeRuleGameConfig(rawByGame[gameId]);
    }
  }
  return {
    version: RULE_CONFIG_VERSION,
    byGame,
  };
}

export function mergeRuleConfigs(
  local: PersistedRuleConfig,
  remote: PersistedRuleConfig
): PersistedRuleConfig {
  const byGame: Partial<Record<GameId, PersistedRuleGameConfig>> = {};
  for (const gameId of GAME_IDS) {
    const localGame = local.byGame[gameId];
    const remoteGame = remote.byGame[gameId];
    if (remoteGame) {
      byGame[gameId] = remoteGame;
    } else if (localGame) {
      byGame[gameId] = localGame;
    }
  }
  return {
    version: RULE_CONFIG_VERSION,
    byGame,
  };
}

export function serializeRuleConfig(config: PersistedRuleConfig): string {
  return JSON.stringify(sanitizeRuleConfig(config));
}

export function readLocalRuleConfig(): PersistedRuleConfig {
  try {
    const raw = localStorage.getItem(RULE_CONFIG_STORAGE_KEY);
    if (!raw) return emptyRuleConfig();
    return sanitizeRuleConfig(JSON.parse(raw));
  } catch {
    return emptyRuleConfig();
  }
}

export function readLocalRuleConfigIfPresent(): PersistedRuleConfig | undefined {
  try {
    if (!localStorage.getItem(RULE_CONFIG_STORAGE_KEY)) return undefined;
    return readLocalRuleConfig();
  } catch {
    return undefined;
  }
}

export function writeLocalRuleConfig(config: PersistedRuleConfig): void {
  try {
    localStorage.setItem(RULE_CONFIG_STORAGE_KEY, serializeRuleConfig(config));
  } catch {
    /* ignore quota / private-mode errors */
  }
}
